/* eslint-disable prettier/prettier */
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import { showToast } from './toastSlice'
import makeRequest from 'src/services/api'
import handleGeneralError from '../helpers/errorHandler'
import {
  fetchTransactions,
  pointsAdjustment,
  bulkActions,
  fetchProductsUrl,
  fetchStatesUrl,
} from 'src/services/urls'

const initialState = {
  loading: false,
  advanceSearch: {
    searchFields: {},
  },
  data: { hasMore: false, page: 1, totalDocs: 0, nextPage: 0 },
  docs: [],
  error: '',
  pageSize: 10,
  activePage: 0,
  stateList: [],
  productList: [],
}

export const fetchAllStatesList = createAsyncThunk(
  'transactions/fetchStates',
  async (_, { dispatch }) => {
    try {
      const response = await makeRequest(fetchStatesUrl(), 'GET')

      return response.data.data
    } catch (e) {
      handleGeneralError(e, { dispatch, showToast })
      return { error: e?.response?.data?.error?.message }
    }
  },
)
export const fetchAllProductsList = createAsyncThunk(
  'transactions/fetchProducts',
  async (_, { dispatch }) => {
    try {
      const response = await makeRequest(fetchProductsUrl(), 'GET')
      return response.data.data
    } catch (e) {
      handleGeneralError(e, { dispatch, showToast })
      return { error: e?.response?.data?.error?.message }
    }
  },
)

export const fetchAllTransactions = createAsyncThunk(
  'transactions/fetchAllTransactions',
  async (data, { dispatch, getState }) => {
    try {
      dispatch(setLoading())
      const { pageSize } = getState().transaction

      const { advanceSearch } = getState().transaction
      const fetchParams = {
        limit: pageSize,
        page: data.page || 1,
      }

      Object.keys(advanceSearch.searchFields).forEach((item) => {
        if (item === 'productFilter' && advanceSearch.searchFields?.[item]?.length > 0) {
          fetchParams.productIds = JSON.stringify(
            advanceSearch.searchFields[item].map((p) => p.value),
          )
        } else if (item === 'stateFilter' && advanceSearch.searchFields?.[item]?.length > 0) {
          fetchParams.stateIds = JSON.stringify(
            advanceSearch.searchFields[item].map((s) => s.value),
          )
        } else {
          fetchParams[item] = advanceSearch.searchFields?.[item]
        }
      })

      const response = await makeRequest(fetchTransactions(fetchParams))

      return response.data.data
    } catch (e) {
      handleGeneralError(e, { dispatch, showToast })
      return { error: e?.response?.data?.error?.message }
    }
  },
)

export const bulkActionsAdjustment = createAsyncThunk(
  'transactions/bulkActionsAdjustment',
  async (data, { dispatch, getState }) => {
    try {
      const response = await makeRequest(bulkActions(), 'POST', data)

      const { displayMessage, statusMessage } = response.data

      if (response.data.statusCode === 200) {
        dispatch(showToast({ title: statusMessage, message: displayMessage, color: 'success' }))
      }

      if (response.data.statusCode === 400) {
        dispatch(showToast({ title: 'Error!', message: displayMessage, color: 'danger' }))
      }
      const { activePage } = getState().transaction

      dispatch(fetchAllTransactions({ page: activePage || 1 }))
      return response.data.data
    } catch (e) {
      handleGeneralError(e, { dispatch, showToast })
      return { error: e?.response?.data?.error?.message }
    }
  },
)

export const adjustPoints = createAsyncThunk(
  'transactions/adjustPoints',
  async (data, { dispatch, getState }) => {
    try {
      const response = await makeRequest(pointsAdjustment(), 'POST', data)

      const { displayMessage, statusMessage } = response.data

      if (response.data.statusCode === 200) {
        dispatch(showToast({ title: 'Failed!', message: displayMessage, color: 'danger' }))
      }

      if (response.data.statusCode === 201) {
        dispatch(showToast({ title: statusMessage, message: displayMessage, color: 'success' }))
      }

      if (response.data.statusCode === 400) {
        dispatch(showToast({ title: 'Error!', message: displayMessage, color: 'danger' }))
      }
      const { activePage } = getState().transaction

      dispatch(fetchAllTransactions({ page: activePage || 1 }))

      return response.data
    } catch (e) {
      handleGeneralError(e, { dispatch, showToast })
    }
  },
)

const transactionSlice = createSlice({
  name: 'transactions',
  initialState,
  reducers: {
    setActivePage: (state, action) => {
      state.activePage = action.payload
    },
    setLoading(state, action) {
      state.loading = true
    },
    setPageSize: (state, action) => {
      state.pageSize = action.payload
    },
    setAdvanceSearch: (state, action) => {
      state.advanceSearch = action.payload
    },
    setSelectTransaction: (state, action) => {
      state.docs = action.payload
    },
    setAdvanceSearchSerialNumber: (state, action) => {
      state.advanceSearch = {
        ...state.advanceSearch,
        searchFields: { ...state.advanceSearch.searchFields, serialNumber: action.payload },
      }
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchAllTransactions.pending, (state, action) => {})
    builder.addCase(fetchAllTransactions.fulfilled, (state, action) => {
      state.loading = false
      state.error = action.payload.error
      if (!action.payload.error) {
        if (action.payload.docs && Array.isArray(action.payload.docs)) {
          state.docs = action.payload.docs.map((t) => {
            return { ...t, select: false }
          })
        } else {
          state.docs = action.payload.docs
        }
        state.data = action.payload
      }
    })
    builder.addCase(fetchAllTransactions.rejected, (state, action) => {
      state.loading = false
      state.data = {}
      state.docs = []
      state.error = action.error.message
    })
    builder.addCase(adjustPoints.pending, (state) => {})
    builder.addCase(adjustPoints.fulfilled, (state, action) => {
      state.docs = []
      state.error = ''
    })
    builder.addCase(adjustPoints.rejected, (state, action) => {
      // state.data = []
      state.error = action.error.message
    })
    builder.addCase(bulkActionsAdjustment.pending, (state) => {})
    builder.addCase(bulkActionsAdjustment.fulfilled, (state, action) => {
      state.docs = []
      state.error = ''
    })
    builder.addCase(bulkActionsAdjustment.rejected, (state, action) => {
      // state.data = []
      state.error = action.error.message
    })
    builder.addCase(fetchAllStatesList.fulfilled, (state, action) => {
      state.stateList = action.payload.reduce((acc, curr) => {
        if (curr.state !== 'All') {
          return [...acc, { label: curr.state, value: curr._id }]
        }
        return acc
      }, [])
    })

    builder.addCase(fetchAllStatesList.rejected, (state, action) => {
      state.error = action.error.message
    })
    builder.addCase(fetchAllProductsList.fulfilled, (state, action) => {
      state.productList = action.payload.map((product) => {
        return { label: product.productName, value: product._id }
      })
    })

    builder.addCase(fetchAllProductsList.rejected, (state, action) => {
      state.error = action.error.message
    })
  },
})

export default transactionSlice.reducer
export const {
  setActivePage,
  setAdvanceSearch,
  setPageSize,
  setSelectTransaction,
  setAdvanceSearchSerialNumber,
  setLoading,
} = transactionSlice.actions
